/**
 * Types and labels for /customer-duplicates.
 *
 * The reason/marker/status CODES are the contract with the API (they come straight off
 * `CustomerDuplicatePair`/`CustomerDuplicateDecision` — `api/src/shared/customer-duplicate-engine.ts`
 * is the one place that decides what they MEAN); the English label lives here and nowhere else,
 * same split as `docuwarePoCheck.ts`'s `FINDING_LABELS`.
 */

export type DuplicateReason = 'BLOCKED_REPLACEMENT' | 'MIGRATION' | 'VAT' | 'BUSINESS_UNIT' | 'SITE' | 'UNCLEAR';
export type MatchScope = 'INTRA' | 'CROSS';
export type MatchLevel = 1 | 2;
export type DecisionStatus = 'OPEN' | 'CONFIRMED' | 'NOT_DUPLICATE' | 'MERGED' | 'WATCH';

export const ALL_REASONS: DuplicateReason[] = ['BLOCKED_REPLACEMENT', 'MIGRATION', 'VAT', 'BUSINESS_UNIT', 'SITE', 'UNCLEAR'];
export const ALL_STATUSES: DecisionStatus[] = ['OPEN', 'CONFIRMED', 'NOT_DUPLICATE', 'MERGED', 'WATCH'];

export const REASON_LABELS: Record<DuplicateReason, string> = {
    BLOCKED_REPLACEMENT: 'Blocked, replaced',
    MIGRATION: 'Migration debris',
    VAT: 'VAT workaround',
    BUSINESS_UNIT: 'Business unit / sales manager',
    SITE: 'Different site',
    UNCLEAR: 'Unclear',
};

/**
 * The matching rules in words. The codes are what `CustomerDuplicatePair.MatchedRules` stores;
 * the definitions are in `context/docs/customer-duplicates.md` → Regelwerk.
 */
export const RULE_LABELS: Record<string, string> = {
    R_VAT_EXACT: 'Same VAT number',
    R_VAT_CORE: 'Same VAT number, different notation',
    R_REGISTER: 'Same official UID (register)',
    R_CODE_PREFIX: 'Same customer code',
    R_NAME_ADDR: 'Same name and postcode',
    R_NAME_CITY: 'Same name and city',
    R_NAME_COUNTRY: 'Same name, same country',
    R_EMAIL_DOMAIN: 'Shared e-mail domain',
    // Not rules, but the other two values of `CustomerIdentityGroupMember.JoinedByRule`.
    ANCHOR: 'Group anchor',
    DECISION: 'Confirmed by a decision',
};

/** `MatchedRules` CSV → readable labels; an unknown code is shown as it is rather than hidden. */
export function ruleLabels(csv: string | null | undefined): string[] {
    return parseMatchedRules(csv).map(r => RULE_LABELS[r] ?? r);
}

export const STATUS_LABELS: Record<DecisionStatus, string> = {
    OPEN: 'Open',
    CONFIRMED: 'Confirmed',
    NOT_DUPLICATE: 'Not a duplicate',
    MERGED: 'Merged',
    WATCH: 'Watch',
};

export const ALL_MARKERS = [
    'BOTH_DORMANT', 'ONE_SIDE_DORMANT', 'BLOCKED_SIDE', 'BOTH_BLOCKED', 'DEAD_SOURCE_SYSTEM',
    'SOURCE_SYSTEM_DIFFERS', 'SALES_MANAGER_UNRESOLVED', 'SALES_MANAGER_DIFFERS',
    'VAT_SUFFIX_TRUNCATED', 'VAT_MISSING_ONE_SIDE', 'VAT_MISSING_BOTH', 'COUNTRY_DIFFERS',
    'CODE_PREFIX_VARIANT', 'VAT_INVALID_SIDE', 'REGISTRY_UID_PROBABLE_BOTH',
    'REGISTRY_UID_ONE_SIDE_PROBABLE',
] as const;
export type DuplicateMarker = typeof ALL_MARKERS[number];

/**
 * Markers that need a dormant side (a dead source system implies one). Since only active accounts
 * are compared (2026-09-29), no current pair can carry them; they stay labelled for older pairs
 * but are not offered as a filter.
 */
export const RETIRED_MARKERS: readonly DuplicateMarker[] = ['BOTH_DORMANT', 'ONE_SIDE_DORMANT', 'DEAD_SOURCE_SYSTEM'];

export const MARKER_LABELS: Record<DuplicateMarker, string> = {
    BOTH_DORMANT: 'Both sides dormant',
    ONE_SIDE_DORMANT: 'One side dormant',
    BLOCKED_SIDE: 'One side blocked',
    BOTH_BLOCKED: 'Both sides blocked',
    DEAD_SOURCE_SYSTEM: 'Dead source system',
    SOURCE_SYSTEM_DIFFERS: 'Source system differs',
    SALES_MANAGER_UNRESOLVED: 'Sales manager unresolved',
    SALES_MANAGER_DIFFERS: 'Sales manager differs',
    VAT_SUFFIX_TRUNCATED: 'VAT suffix was truncated',
    VAT_MISSING_ONE_SIDE: 'VAT missing on one side',
    VAT_MISSING_BOTH: 'VAT missing on both sides',
    COUNTRY_DIFFERS: 'Country differs',
    CODE_PREFIX_VARIANT: 'Customer-code prefix variant',
    VAT_INVALID_SIDE: 'A register says one VAT number is invalid',
    REGISTRY_UID_PROBABLE_BOTH: 'Register UID only probable on both sides',
    REGISTRY_UID_ONE_SIDE_PROBABLE: 'Register UID probable on one side',
};

/** The 8 companies in scope (masterplan §3a) — etl.DolderCompany.CompanyName, trimmed. */
export const COMPANY_LABELS: Record<string, string> = {
    bin: 'Bigler International (bin)',
    dbi: 'Dolder Bigler AG (dbi)',
    dch: 'Dolder Switzerland (dch)',
    dfr: 'Eneco (dfr)',
    dit: 'Dolder Italia (dit)',
    dpt: 'Dolder Portugal (dpt)',
    dsp: 'Dolder Espana (dsp)',
    dtr: 'Dolder Turkey (dtr)',
};
export const ALL_COMPANIES = Object.keys(COMPANY_LABELS);

export function parseSecondaryReasons(csv: string | null | undefined): string[] {
    return (csv ?? '').split(',').map(s => s.trim()).filter(s => s !== '');
}

export function parseMatchedRules(csv: string | null | undefined): string[] {
    return (csv ?? '').split(',').map(s => s.trim()).filter(s => s !== '');
}

export interface PairRow {
    pairKey: string;
    customerIdA: string;
    customerIdB: string;
    companyCodeA: string;
    companyCodeB: string;
    customerCodeA: string | null;
    customerCodeB: string | null;
    customerNameA: string | null;
    customerNameB: string | null;
    isBlockedA: boolean;
    isBlockedB: boolean;
    scope: MatchScope;
    matchLevel: MatchLevel;
    matchedRules: string;
    primaryReason: DuplicateReason;
    secondaryReasons: string | null;
    score: number;
    firstSeenAt: string;
    lastSeenAt: string;
    status: DecisionStatus;
    reasonOverride: DuplicateReason | null;
    keepCustomerId: string | null;
    decidedBy: string | null;
    decidedAt: string | null;
    lastMovementA: string | null;
    lastMovementB: string | null;
    openItemsA: number;
    openItemsB: number;
}

export interface ReasonBucket {
    total: number;
    bothDormant: number;
    oneSideDormant: number;
}

export interface RunSummary {
    runId: number;
    startedAt: string;
    finishedAt: string | null;
    /** SUCCESS or PARTIAL. PARTIAL means the run is INCOMPLETE — `error` says how (migration 182). */
    status: string;
    customersScanned: number;
    pairsFound: number;
    pairsNew: number;
    /** Pairs this run stopped finding. Flagged `IsCurrent = 0`, never deleted. */
    pairsGone: number;
    /** Plain text on a PARTIAL run, null on a complete one. */
    error: string | null;
    /** Raw JSON string from CustomerDuplicateRun.RejectedByRule — parsed client-side (F6). */
    rejectedByRule: string | null;
    reasonCounts: string | null;
    /** Raw JSON from CustomerDuplicateRun.Diagnostics — read via `parseRunScope`. */
    diagnostics?: string | null;
}

/** What the latest run left out before comparing (2026-09-29). Null on runs from before that. */
export interface RunScope {
    customersLoaded: number;
    customersCompared: number;
    closed: number;
    zzzName: number;
    /** No positive net revenue in the window (key `NO_REVENUE`; run 45 still wrote `DORMANT`). */
    noRevenue: number;
}

export function parseRunScope(run: RunSummary | null | undefined): RunScope | null {
    if (!run?.diagnostics) return null;
    try {
        const d = JSON.parse(run.diagnostics) as {
            customersLoaded?: number;
            excludedAccounts?: { CLOSED?: number; ZZZ_NAME?: number; NO_REVENUE?: number; DORMANT?: number };
        };
        if (d.excludedAccounts === undefined || d.customersLoaded === undefined) return null;
        return {
            customersLoaded: d.customersLoaded,
            customersCompared: run.customersScanned,
            closed: d.excludedAccounts.CLOSED ?? 0,
            zzzName: d.excludedAccounts.ZZZ_NAME ?? 0,
            noRevenue: d.excludedAccounts.NO_REVENUE ?? d.excludedAccounts.DORMANT ?? 0,
        };
    } catch {
        return null;
    }
}

export interface PairListSummary {
    byReason: Record<DuplicateReason, ReasonBucket>;
    byCompany: Record<string, number>;
    byLevel: Record<string, number>;
    byStatus: Record<string, number>;
    affectedAccounts: number;
    openItems: number;
    openAmountEur: number;
    accountsWithOpenItems: number;
    revenueEur: number;
    revenueWindowMonths: number;
    accountsWithRevenue: number;
    latestRun: RunSummary | null;
    registryCoverage: { checked: number; total: number };
    /** Undecided pairs the latest run no longer finds — unfiltered, the size of `presence=GONE`. */
    goneNotDecided: number;
}

/** What the navigation badge counts — `GET /customer-duplicates/attention-summary`. */
export interface DuplicateAttentionSummary {
    newSinceLastRun: number;
    goneNotDecided: number;
    openTotal: number;
    lastRun: (RunSummary & { source: string; elapsedMs: number | null }) | null;
}

export interface PairListResult {
    items: PairRow[];
    summary: PairListSummary;
    total: number;
}

export interface PairFilters {
    reason: string;
    company: string;
    level: string;
    scope: string;
    marker: string;
    status: string;
    onlyNew: boolean;
    /** CURRENT (the work queue, default) · GONE (no longer found) · ALL. */
    presence: string;
    search: string;
}

export const EMPTY_FILTERS: PairFilters = {
    reason: '', company: '', level: '', scope: '', marker: '', status: 'OPEN', onlyNew: false,
    presence: 'CURRENT', search: '',
};

export type PairSortField = 'score' | 'primaryReason' | 'level' | 'scope' | 'company' | 'status' | 'firstSeen' | 'lastSeen';

// --- Detail view -------------------------------------------------------------------------------

export interface CustomerRecord {
    CustomerID: string;
    CustomerCode: string | null;
    CustomerName: string | null;
    Classification: string | null;
    CountryISO3: string | null;
    CurrencyCode: string | null;
    Street: string | null;
    ZipCode: string | null;
    City: string | null;
    State: string | null;
    CreditLimitLCY: number | null;
    SalesManagerID: string | null;
    BusinessUnitCode: string | null;
    SalesArea: string | null;
    Email: string | null;
    Phone: string | null;
    Incoterm: string | null;
    PaymentTermCode: string | null;
    CustomerCategoryID: string | null;
    VATNr: string | null;
    LanguageCode: string | null;
    IsBlocked: boolean;
    BlockedFor: string | null;
    SourceSystem: string | null;
}

export interface NormalizedFacts {
    nameDisplay: string;
    nameKey: string;
    vatNormalized: string;
    vatCore: string;
    vatSuffix: string | null;
    vatTruncated: boolean;
    addressKey: { streetKey: string; zipKey: string; cityKey: string; country: string };
}

export interface PairSide {
    customer: CustomerRecord;
    normalized: NormalizedFacts;
    lastMovement: string | null;
    revenueEur24m: number;
    openItems: number;
    openAmountEur: number;
    customer360Url: string;
    arDunningUrl: string;
}

export interface VatValidationRow {
    customerId: string;
    source: 'UID' | 'VIES' | 'FORMAT';
    status: string;
    isValid: boolean | null;
    vatNormalized: string | null;
    registeredName: string | null;
    registeredSeat: string | null;
    registeredUid: string | null;
    uidConfidence: 'CONFIRMED' | 'PROBABLE' | null;
    checkedAt: string;
    errorCode: string | null;
    errorDetail: string | null;
}

export interface RegistryMatchRow {
    customerId: string;
    source: 'ZEFIX' | 'UID_SEARCH' | 'UID_LOOKUP';
    uid: string | null;
    registeredName: string | null;
    seat: string | null;
    legalForm: string | null;
    registryStatus: string | null;
    matchQuality: string;
    checkedAt: string;
}

export interface AuditRow {
    field: string;
    oldValue: string | null;
    newValue: string | null;
    changedBy: string;
    changedAt: string;
}

export interface PairDetail {
    pair: {
        pairKey: string; customerIdA: string; customerIdB: string;
        companyCodeA: string; companyCodeB: string; scope: MatchScope; matchLevel: MatchLevel;
        matchedRules: string; primaryReason: DuplicateReason; secondaryReasons: string | null;
        score: number; firstSeenAt: string; lastSeenAt: string; isCurrent: boolean;
    };
    decision: {
        status: DecisionStatus; reasonOverride: DuplicateReason | null; keepCustomerId: string | null;
        comment: string | null; decidedBy: string; decidedAt: string;
    } | null;
    sideA: PairSide;
    sideB: PairSide;
    registry: {
        vatValidation: VatValidationRow[];
        matches: RegistryMatchRow[];
        attribution: Record<string, string>;
    };
    audit: AuditRow[];
}

export interface DecideRequest {
    status: DecisionStatus;
    reasonOverride?: string | null;
    keepCustomerId?: string | null;
    comment?: string | null;
}

// --- P4: the group-wide identity ---------------------------------------------------------------
//
// One row per customer identity across all eight companies. The codes come off
// `[Reporting].[CustomerIdentityGroup]` and the read-time warning computation in
// `api/src/functions/customer-duplicates/shared/group-metrics.ts`; the English labels live here
// and nowhere else, same split as the pair vocabulary above.

/** `blockedSplit` / `overdueSplit` arrive as SQL BIT, i.e. as NUMBERS (handoff P3 § 4.1). */
export interface GroupRow {
    groupId: number;
    groupKey: string;
    displayName: string | null;
    registeredUid: string | null;
    countryISO3: string | null;
    memberCount: number;
    companyCount: number;
    /** 'dch,dit,dsp' — sorted, de-duplicated. */
    companyCodes: string;
    updatedAt: string;
    openItems: number;
    openAmountEur: number;
    overdueAmountEur: number;
    maxDaysOverdue: number;
    revenueEur: number;
    lastMovement: string | null;
    blockedMembers: number;
    dunningLevelSent: number;
    hubspotCompanies: number;
    blockedSplit: number;
    overdueSplit: number;
}

export interface GroupMemberRow {
    customerId: string;
    companyCode: string;
    customerCode: string | null;
    customerName: string | null;
    countryISO3: string | null;
    city: string | null;
    vatNr: string | null;
    sourceSystem: string | null;
    businessUnitCode: string | null;
    joinedByRule: string;
    isPrimary: boolean;
    isBlocked: number;
    blockedFor: string | null;
    lastMovement: string | null;
    revenueEur: number;
    openItems: number;
    openAmountEur: number;
    overdueAmountEur: number;
    maxDaysOverdue: number;
    dunningLevelSent: number;
    hubspotCompanies: number;
    isProblemBlocked: number;
    isProblemOverdue: number;
    isActiveAccount: number;
}

export interface GroupCompanyRoll {
    companyCode: string;
    hasBlocked: number;
    hasOverdue: number;
    hasClear: number;
}

export interface GroupWarningConfig {
    overdueDays: number;
    minOpenAmountEur: number;
    requireActiveCounterpart: boolean;
    inactivityMonths: number;
    revenueWindowMonths: number;
    strongRules?: string[];
}

export interface GroupBuildSummary {
    runId: number;
    startedAt: string;
    finishedAt: string | null;
    status: string;
    source: string;
    edgesConsidered: number;
    edgesAccepted: number;
    groupsBuilt: number;
    membersAssigned: number;
    multiCompanyGroups: number;
    largestGroupSize: number;
    refusedNotDuplicate: number;
    refusedMaxSize: number;
    refusedMaxChain: number;
    /** Raw JSON — parsed client-side, like RunSummary.rejectedByRule (F6). */
    refusals: string | null;
    diagnostics: string | null;
    elapsedMs: number | null;
}

export interface GroupListSummary {
    totals: {
        groups: number; members: number; multiCompanyGroups: number; openItems: number;
        openAmountEur: number; overdueAmountEur: number; revenueEur: number;
        hubspotCompanies: number; blockedMembers: number;
    };
    warning: {
        anyWarning: number; blockedSplit: number; overdueSplit: number; noWarning: number;
        warningOpenAmountEur: number;
    };
    byCompany: Record<string, number>;
    config: GroupWarningConfig;
    lastBuild: GroupBuildSummary | null;
}

export interface GroupListResult {
    items: GroupRow[];
    summary: GroupListSummary;
    total: number;
}

export interface GroupEvidenceRow {
    pairKey: string;
    customerIdA: string;
    customerIdB: string;
    matchedRules: string;
    primaryReason: DuplicateReason;
    secondaryReasons: string | null;
    matchLevel: MatchLevel;
    scope: MatchScope;
    score: number;
    isCurrent: number;
    status: DecisionStatus;
}

export interface GroupDecisionRow {
    pairKey: string;
    customerIdA: string;
    customerIdB: string;
    status: DecisionStatus;
    reasonOverride: DuplicateReason | null;
    decidedBy: string;
    decidedAt: string;
    comment: string | null;
}

export interface GroupSuggestionRow {
    pairKey: string;
    matchedRules: string;
    primaryReason: DuplicateReason;
    matchLevel: MatchLevel;
    scope: MatchScope;
    score: number;
    status: DecisionStatus;
    insideCustomerId: string;
    outsideCustomerId: string;
    outsideCustomerName: string | null;
    outsideCompanyCode: string;
    outsideGroupId: number | null;
    hasStrongRule: number;
    /** How many pairs connect this outside account to the group. One row is shown for all of them. */
    matchingPairs: number;
}

export interface GroupHubspotRow {
    customerId: string;
    hubspotCompanyId: string;
    linkToCrm: string | null;
    hubspotCompanyName: string | null;
    hubspotCity: string | null;
    hubspotCountry: string | null;
    lifecycleStage: string | null;
}

export interface GroupTotals {
    openItems: number;
    openAmountEur: number;
    overdueAmountEur: number;
    revenueEur: number;
    hubspotCompanies: number;
    blockedMembers: number;
    maxDaysOverdue: number;
    dunningLevelSent: number;
}

export interface GroupDetail {
    group: GroupRow;
    members: GroupMemberRow[];
    companyRoll: GroupCompanyRoll[];
    /** Summed from `members` in the API. */
    totals: GroupTotals;
    /** The database's own aggregate of the same rows. The two must agree. */
    rollup: GroupTotals;
    evidence: GroupEvidenceRow[];
    decisions: GroupDecisionRow[];
    suggestions: GroupSuggestionRow[];
    hubspot: GroupHubspotRow[];
    config: GroupWarningConfig;
}

/** What `by-customer` answers — `found: false` for the vast majority of customers. */
export interface GroupForCustomer {
    found: boolean;
    customerId: string;
    groupId?: number;
    groupKey?: string;
    displayName?: string | null;
    memberCount?: number;
    companyCount?: number;
    companyCodes?: string;
    openItems?: number;
    openAmountEur?: number;
    overdueAmountEur?: number;
    blockedMembers?: number;
    blockedSplit?: number;
    overdueSplit?: number;
    overdueDays?: number;
    url?: string;
}

export interface GroupFilters {
    company: string;
    /** '' | 'ANY' | 'BLOCKED' | 'OVERDUE' | 'NONE' */
    warning: string;
    multiCompany: boolean;
    search: string;
}

export const EMPTY_GROUP_FILTERS: GroupFilters = {
    company: '', warning: '', multiCompany: false, search: '',
};

export type GroupSortField =
    'warning' | 'openAmount' | 'overdueAmount' | 'revenue' | 'members' | 'companies' | 'lastMovement' | 'name';

export function parseCompanyCodes(csv: string | null | undefined): string[] {
    return (csv ?? '').split(',').map(s => s.trim()).filter(s => s !== '');
}
